import { useState } from "react";
import { motion } from "framer-motion";
import {
    Phone,
    Mail,
    MapPin,
    Clock,
    Send,
    MessageCircle,
    CheckCircle,
    Sparkles,
    AlertCircle,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
} from "../../components/ui/AnimatedSection";
import { contactService } from "../../services/contact.service";

const initialContactInfo = [
    {
        icon: Phone,
        title: "Phone",
        details: ["Loading..."],
        action: "",
    },
    {
        icon: Mail,
        title: "Email",
        details: ["official@sevaconsultancy.co.in"],
        action: "mailto:official@sevaconsultancy.co.in",
    },
    {
        icon: MapPin,
        title: "Office",
        details: ["Loading..."],
        action: null,
    },
    {
        icon: Clock,
        title: "Hours",
        details: [
            "Mon - Fri: 9:00 AM - 6:00 PM",
            "Saturday: 10:00 AM - 4:00 PM",
        ],
        action: null,
    },
];

const services = [
    "Health Insurance",
    "Life Insurance",
    "Group Insurance",
    "Term Plans",
    "Financial Planning",
    "Mutual Funds",
    "Stocks & ETFs",
    "Retirement Planning",
    "Other",
];

import { useEffect } from "react";
import { companyService } from "../../services/company.service";

export default function ContactPage() {
    const [contactDetails, setContactDetails] = useState(initialContactInfo);

    useEffect(() => {
        const fetchCompanyDetails = async () => {
            try {
                const company = await companyService.getCompanyDetails();
                if (company) {
                    const newContactInfo = [...initialContactInfo];

                    // Phone
                    newContactInfo[0].details = [company.mobileNumber];
                    newContactInfo[0].action = `tel:${company.mobileNumber}`;

                    // Email
                    newContactInfo[1].details = [company.email];
                    newContactInfo[1].action = `mailto:${company.email}`;

                    // Address
                    newContactInfo[2].details = [company.address];

                    setContactDetails(newContactInfo);
                }
            } catch (error) {
                console.error("Failed to load company details", error);
            }
        };
        fetchCompanyDetails();
    }, []);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateForm = () => {
        const newErrors: string[] = [];

        // Name validation
        if (!formData.name.trim()) newErrors.push("Name is required");

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.push("Email is required");
        } else if (!emailRegex.test(formData.email)) {
            newErrors.push("Please enter a valid email address");
        }

        // Phone validation (10 digits)
        const phoneRegex = /^\d{10}$/;
        if (!formData.phone.trim()) {
            newErrors.push("Phone number is required");
        } else if (!phoneRegex.test(formData.phone.replace(/\D/g, ''))) {
            newErrors.push("Please enter a valid 10-digit phone number");
        }

        if (!formData.message.trim()) newErrors.push("Message is required");

        return newErrors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(validationErrors[0]); // Show first error
            return;
        }

        setIsSubmitting(true);

        try {
            // Map form data to API format
            await contactService.submitContact({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                subject: formData.service || "General Inquiry",
                message: formData.message,
            });

            setIsSubmitting(false);
            setIsSubmitted(true);
            setTimeout(() => {
                setIsSubmitted(false);
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    service: "",
                    message: "",
                });
            }, 3000);
        } catch (err) {
            setIsSubmitting(false);
            const errorMessage =
                err instanceof Error ? err.message : "Failed to submit form. Please try again.";
            setError(errorMessage);
            console.error("Contact form error:", err);
        }
    };

    return (
        <div className="overflow-hidden">
            {/* Hero */}
            <section className="relative py-32 bg-cream">
                <div className="absolute inset-0 bg-pattern" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#d4b86a]/5 rounded-full blur-3xl" />

                <div className="container-custom px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl mx-auto text-center"
                    >
                        <span className="badge mb-4">
                            <Sparkles className="w-3 h-3 mr-2" />
                            Get in Touch
                        </span>
                        <h1 className="text-gray-900 mb-6">
                            Let's Start a
                            <span className="block mt-1 gradient-text">
                                Conversation
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Have questions about insurance or investments? We're
                            here to help. Reach out and let's discuss how we can
                            secure your financial future.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Cards */}
            <section className="py-8 bg-white -mt-16 relative z-10">
                <div className="container-custom px-6">
                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {contactDetails.map((info) => (
                            <StaggerItem key={info.title}>
                                <motion.div
                                    whileHover={{ y: -4 }}
                                    className="glass-card rounded-xl p-5 h-full"
                                >
                                    <div className="icon-box w-10 h-10 mb-3">
                                        <info.icon className="w-5 h-5 text-[#c9a84c]" />
                                    </div>
                                    <h3 className="text-xs uppercase tracking-wider text-[#c9a84c] mb-1">
                                        {info.title}
                                    </h3>
                                    {info.details.map((detail, index) => (
                                        <p
                                            key={index}
                                            className="text-sm text-gray-600"
                                        >
                                            {info.action && index === 0 ? (
                                                <a
                                                    href={info.action}
                                                    className="hover:text-[#c9a84c] transition-colors"
                                                >
                                                    {detail}
                                                </a>
                                            ) : (
                                                detail
                                            )}
                                        </p>
                                    ))}
                                </motion.div>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Form */}
                        <AnimatedSection>
                            <div className="glass-card rounded-2xl p-8 card-corner-accent">
                                <div className="mb-8">
                                    <h2 className="text-gray-900 mb-2">
                                        Send us a Message
                                    </h2>
                                    <p className="text-gray-500">
                                        We'll respond within 24 hours
                                    </p>
                                </div>

                                {isSubmitted ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-center py-12"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                            <CheckCircle className="w-8 h-8 text-emerald-600" />
                                        </div>
                                        <h3 className="text-xl font-display text-gray-900 mb-2">
                                            Message Sent!
                                        </h3>
                                        <p className="text-gray-500">
                                            Thank you for reaching out. We'll
                                            contact you soon.
                                        </p>
                                    </motion.div>
                                ) : error ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-center py-12"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                            <AlertCircle className="w-8 h-8 text-red-600" />
                                        </div>
                                        <h3 className="text-xl font-display text-gray-900 mb-2">
                                            Error
                                        </h3>
                                        <p className="text-gray-500 mb-4">{error}</p>
                                        <button
                                            onClick={() => setError(null)}
                                            className="btn-primary text-sm"
                                        >
                                            Try Again
                                        </button>
                                    </motion.div>
                                ) : (
                                    <form
                                        onSubmit={handleSubmit}
                                        className="space-y-5"
                                    >
                                        <div className="grid md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-[#d4b86a] focus:outline-none focus:ring-1 focus:ring-[#d4b86a]/20 transition-colors"
                                                    placeholder="Your name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">
                                                    Phone Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-[#d4b86a] focus:outline-none focus:ring-1 focus:ring-[#d4b86a]/20 transition-colors"
                                                    placeholder="Your phone"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-[#d4b86a] focus:outline-none focus:ring-1 focus:ring-[#d4b86a]/20 transition-colors"
                                                placeholder="Your email"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">
                                                Service Interest
                                            </label>
                                            <select
                                                name="service"
                                                value={formData.service}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:border-[#d4b86a] focus:outline-none focus:ring-1 focus:ring-[#d4b86a]/20 transition-colors cursor-pointer"
                                            >
                                                <option value="">
                                                    Select a service
                                                </option>
                                                {services.map((service) => (
                                                    <option
                                                        key={service}
                                                        value={service}
                                                    >
                                                        {service}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">
                                                Message
                                            </label>
                                            <textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                rows={4}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-[#d4b86a] focus:outline-none focus:ring-1 focus:ring-[#d4b86a]/20 transition-colors resize-none"
                                                placeholder="Tell us about your requirements"
                                            />
                                        </div>

                                        <motion.button
                                            type="submit"
                                            disabled={isSubmitting}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className="w-full btn-primary inline-flex items-center justify-center space-x-2 disabled:opacity-70"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    <span>Sending...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>Send Message</span>
                                                    <Send className="w-4 h-4" />
                                                </>
                                            )}
                                        </motion.button>
                                    </form>
                                )}
                            </div>
                        </AnimatedSection>

                        {/* Info Side */}
                        <AnimatedSection direction="right">
                            <div className="space-y-6">
                                {/* Quick Contact Card */}
                                <div className="glass-card rounded-2xl p-6 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4b86a]/5 rounded-full blur-2xl" />
                                    <div className="relative">
                                        <MessageCircle className="w-8 h-8 text-[#c9a84c] mb-4" />
                                        <h3 className="text-lg font-display text-gray-900 mb-2">
                                            Need Quick Help?
                                        </h3>
                                        <p className="text-gray-500 text-sm mb-5 leading-relaxed">
                                            Our team is available during
                                            business hours for immediate
                                            assistance.
                                        </p>
                                        <motion.a
                                            href="tel:+919825959058"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className="btn-primary inline-flex items-center space-x-2"
                                        >
                                            <Phone className="w-4 h-4" />
                                            <span>Call Now</span>
                                        </motion.a>
                                    </div>
                                </div>

                                {/* Map 
                                <div className="rounded-2xl h-64 overflow-hidden shadow-lg">
                                    <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3690.608513008239!2d73.15025854297345!3d22.33064092477612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395fc9003f68ccdf%3A0x4a30662c37e90d02!2sOm%20Flat%20Block%20No%2014!5e0!3m2!1sen!2sin!4v1769549661984!5m2!1sen!2sin"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen
                                        loading="lazy"
                                    />
                                </div>*/}
                            </div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-12">
                        <span className="badge mb-4">FAQ</span>
                        <h2 className="text-gray-900 mb-2">
                            Frequently Asked{" "}
                            <span className="gradient-text">Questions</span>
                        </h2>
                    </AnimatedSection>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {[
                            {
                                q: "How do I get started?",
                                a: "Fill out the form or call us to schedule a complimentary consultation.",
                            },
                            {
                                q: "Is there a fee for initial consultation?",
                                a: "No, we offer a completely free initial consultation to understand your needs.",
                            },
                            {
                                q: "How long does claim processing take?",
                                a: "Claims are typically processed within 7-15 working days depending on type.",
                            },
                            {
                                q: "Can I track my investments online?",
                                a: "Yes, we provide regular portfolio updates and schedule periodic reviews.",
                            },
                        ].map((faq, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="glass-card rounded-xl p-5 hover:border-[#d4b86a]/30 transition-colors"
                            >
                                <h3 className="text-gray-900 font-medium mb-1">
                                    {faq.q}
                                </h3>
                                <p className="text-sm text-gray-500">{faq.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 section-dark">
                <div className="container-custom px-6 text-center relative z-10">
                    <AnimatedSection>
                        <h2 className="text-white mb-4">
                            Ready to Secure{" "}
                            <span className="gradient-text">Your Future</span>?
                        </h2>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Take the first step towards financial freedom today
                        </p>
                        <motion.a
                            href={contactDetails[0].action || "tel:+919825959058"}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="btn-primary inline-flex items-center space-x-2"
                        >
                            <Phone className="w-4 h-4" />
                            <span>Call {contactDetails[0].details[0] === "Loading..." ? "+91 98259 59058" : contactDetails[0].details[0]}</span>
                        </motion.a>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
}
